import { useState, useCallback, useEffect, useMemo } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import { useEditVehicleStock } from "hooks/stockHook"
import { useToastMessage } from "hooks/useToastMessage"
import ButtonTooltip from "components/Common/ButtonTooltip"
import LabelButtons from "components/Common/LabelButtons"
import WarningAlert from "components/Common/WarningAlert"
import { useVehicleForm } from "hooks/useVehicleForm"
import { modelToModelIdsNumber } from "utils/modelUtils"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

export default function StockFeaturesUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(prev => !prev)

  const { handleResponse } = useToastMessage()
  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  const validationSchema = Yup.object({
    features: Yup.array().of(Yup.number()),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const res = await editStock({
          id: stock?.id,
          featureIds: values.features,
        })
        handleResponse(res, "Features updated successfully.", true)
        if (res.success) {
          toggle()
          resetForm()
        }
      } catch (error) {
        console.error("Failed to update features:", error)
      }
    },
    [editStock, stock, handleResponse]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      features: stock?.features?.map(f => f.id) || [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const { handleParams, optionLoading, featuresData, rawData } = useVehicleForm({
    stockResponse: stock,
    isEdit: true,
    formik: validation,
  })

  const modelSlug = stock?.model?.slug || ""

  useEffect(() => {
    if (modelSlug) handleParams("model", modelSlug)
  }, [modelSlug, handleParams])

  // Compute features list only once when rawData changes
  const allFeatures = useMemo(() => rawData?.features || [], [rawData?.features])

  // Auto-check model-associated features (only when adding new stock)
  useEffect(() => {
    if (!stock?.id && featuresData?.length > 0) {
      const modelFeatureIds = modelToModelIdsNumber(featuresData)
      validation.setFieldValue("features", modelFeatureIds)
    }
  }, [featuresData, stock?.id]) // ✅ limited deps, no endless rerender

  const featuresBtnData = useMemo(
    () => [
      {
        type: "add",
        btnId: "add-feature",
        btnName: "Add feature",
        onClick: () => console.log("Navigate to feature list"),
      },
    ],
    []
  )

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-features"
        btnName="Edit Features"
      />

      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Features</ModalHeader>
        <ModalBody>
          <WarningAlert message="Occasionally, a double-click action is required to deselect/uncheck items." />

          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col sm={12} md={8} lg={9} xl={10} className="mb-3">
                <LabelButtons name="Features" buttonsData={featuresBtnData} />
                <ButtonCheckboxList
                  items={allFeatures}
                  name="features"
                  itemName="features"
                  selected={validation.values.features || []}
                  onChange={newSelected =>
                    validation.setFieldValue("features", newSelected)
                  }
                  loading={optionLoading}
                />
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button type="submit" color="success" disabled={stockEditLoading}>
                Save Changes {stockEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
